import { useTranslation } from "react-i18next";
import U2000BasicDialog from "./U2000BasicDialog";
import { FaSave, FaUndoAlt } from "react-icons/fa";
import { useRef, useEffect } from "react";

import type { ReactNode, RefObject } from "react";

/**
 * Width type for dialog sizing
 */
type WidthProp = "small" | "medium" | "large" | "x-large" | string;

const saveButtonClass = `inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg shadow-xs transition-all duration-200
  hover:bg-green-700 hover:shadow-md 
  focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:ring-offset-2 
  active:bg-green-800 active:scale-95 
  disabled:cursor-not-allowed disabled:bg-gray-400 disabled:text-gray-200 disabled:opacity-60 disabled:shadow-none`;

const cancelButtonClass = `inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-xs transition-all duration-200
  hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 hover:shadow-md 
  focus:outline-hidden focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 
  active:bg-gray-100 active:scale-95`;

interface U2000SaveCancelDialogProps {
  isOpen: boolean;
  onClose: (action: "save" | "cancel") => void;
  title: ReactNode;
  children?: ReactNode;
  /**
   * Dialog width - can be a preset ("small" | "medium" | "large" | "x-large")
   * or any Tailwind width class (e.g., "w-[700px]", "max-w-2xl")
   * @default "medium"
   */
  width?: WidthProp;
  /**
   * Dialog height - any Tailwind height class (e.g., "h-[600px]", "max-h-[80vh]")
   * @default "max-h-[90vh]"
   */
  height?: string;
  /**
   * Whether the form is valid (controls Save button state)
   * @default true
   */
  isValid?: boolean;
  /**
   * Whether the dialog is in read-only mode (shows only Close button)
   * @default false
   */
  readOnly?: boolean;
  /**
   * Ref to an element that should receive focus when the dialog opens
   * If not provided, the first focusable element will be focused
   */
  defaultFocusRef?: RefObject<HTMLElement | HTMLInputElement | HTMLButtonElement | null>;
  /**
   * Si true : pas de scroll/troncature sur la zone formulaire (overflow visible), pour calendriers / listes en position absolue.
   * À activer sur les dialogues compacts ; les formulaires très longs préfèrent false (défaut).
   */
  contentOverflowVisible?: boolean;
  /**
   * Contenu additionnel aligné à gauche dans le pied de page (même zone que Fermer / Enregistrer / Annuler).
   * Ex. bouton d'action secondaire (« Tester le modèle »).
   */
  footerStartSlot?: ReactNode;
}

function U2000SaveCancelDialog({
  isOpen,
  onClose,
  title,
  children,
  width,
  height,
  isValid = true,
  readOnly = false,
  defaultFocusRef,
  contentOverflowVisible = false,
  footerStartSlot,
}: U2000SaveCancelDialogProps) {
  const { t } = useTranslation();

  // Ref to the dialog DOM node used for trapping focus
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Helper: get focusable elements inside the dialog
  const getFocusableElements = () => {
    if (!dialogRef.current) return [] as HTMLElement[];
    const selectors = ["a[href]", "button:not([disabled])", "input:not([disabled])", "select:not([disabled])", "textarea:not([disabled])", '[tabindex]:not([tabindex="-1"])'];
    const nodeList = dialogRef.current.querySelectorAll(selectors.join(","));
    return Array.prototype.slice.call(nodeList) as HTMLElement[];
  };

  // Trap Tab / Shift+Tab inside the dialog so focus doesn't escape
  const handleTrapKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return;

    const focusable = getFocusableElements().filter((el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length));

    if (focusable.length === 0) {
      e.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    // If nothing inside has focus, move to first
    if (!dialogRef.current?.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
      return;
    }

    if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    }
  };

  // When dialog opens, ensure focus is inside
  useEffect(() => {
    if (isOpen) {
      // Prefer focusing the defaultFocusRef element if present
      if (defaultFocusRef?.current) {
        setTimeout(() => defaultFocusRef.current && defaultFocusRef.current.focus(), 0);
        return;
      }

      // Otherwise, focus the first focusable element
      const focusable = getFocusableElements();
      if (focusable.length > 0 && !dialogRef.current?.contains(document.activeElement)) {
        setTimeout(() => focusable[0].focus(), 0);
      }
    }
  }, [isOpen, defaultFocusRef]);

  if (!isOpen) return null;

  // Adapter to match U2000BasicDialog's onClose signature
  const handleClose = (reason: string) => {
    if (reason === "save" || reason === "cancel") {
      onClose(reason);
    } else {
      onClose("cancel");
    }
  };

  const sectionScrollClass = contentOverflowVisible ? "overflow-visible" : "overflow-y-auto min-h-0";

  return (
    <U2000BasicDialog isOpen={isOpen} onClose={handleClose} title={title} width={width} height={height} contentOverflowVisible={contentOverflowVisible}>
      <div ref={dialogRef} onKeyDown={handleTrapKeyDown} className="flex flex-col h-full focus:outline-hidden" tabIndex={-1} role="form" aria-label="Formulaire de dialogue">
        {/* Content Area - with proper scroll handling */}
        <section className={`flex-1 px-6 py-4 ${sectionScrollClass}`}>{children}</section>

        {/* Actions Footer */}
        <footer className="flex items-center justify-between gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-2xl shrink-0">
          <div className="flex items-center gap-3">{footerStartSlot}</div>
          <div className="flex items-center gap-3">
          {readOnly ? (
            <button
              className={cancelButtonClass}
              onClick={() => handleClose("cancel")}
              aria-label="Fermer"
            >
              <span>{t("dlgLblClose", { defaultValue: "Fermer" })}</span>
            </button>
          ) : (
            <>
              <button
                className={cancelButtonClass}
                onMouseDown={(e) => {
                  // Use onMouseDown for Cancel to prevent input onBlur from triggering before click
                  e.preventDefault();
                  handleClose("cancel");
                }}
                onClick={(e) => {
                  // Prevent default onClick behavior when using onMouseDown
                  e.preventDefault();
                }}
                aria-label="Annuler les modifications"
              >
                <FaUndoAlt className="w-4 h-4" />
                <span>{t("dlgLblCancel")}</span>
              </button>

              <button
                className={saveButtonClass}
                onClick={() => handleClose("save")}
                disabled={!isValid}
                aria-label={isValid ? "Sauvegarder les modifications" : "Sauvegarder indisponible - formulaire invalide"}
              >
                <FaSave className="w-4 h-4" />
                <span>{t("dlgLblSave")}</span>
              </button>
            </>
          )}
          </div>
        </footer>
      </div>
    </U2000BasicDialog>
  );
}

export default U2000SaveCancelDialog;
